import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, Network, Search, Users, X } from 'lucide-react';
import type { TeamMember } from '../services/api';
import { OrganizationApiError } from './api';
import { descendantIds } from './graph';
import type { OrganizationPosition, OrganizationSnapshot } from './model';
import { PersonAvatar, type PeopleSelectOption } from './PeopleSelect';
import './RoleQuickEditor.css';

export interface RoleQuickEditorProps {
  companyId: string;
  snapshot: OrganizationSnapshot;
  roleId: string;
  members: TeamMember[];
  mode: 'people' | 'reporting';
  mutate: (
    path: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    data?: Record<string, unknown>
  ) => Promise<unknown>;
  canEdit: boolean;
  onClose: () => void;
  onRefresh: () => void | Promise<unknown>;
}

function reportingLevel(snapshot: OrganizationSnapshot, parentId: string, roleId: string) {
  const visited = new Set([roleId]);
  let current = parentId;
  let level = 1;
  while (current) {
    if (visited.has(current)) return null;
    visited.add(current);
    const parent = snapshot.positions.find((role) => role.id === current);
    if (!parent) return null;
    level++;
    current = parent.parentPositionId || '';
  }
  return level;
}

export default function RoleQuickEditor({
  companyId,
  snapshot,
  roleId,
  members,
  mode,
  mutate,
  canEdit,
  onClose,
  onRefresh,
}: RoleQuickEditorProps) {
  const role = snapshot.positions.find((item) => item.id === roleId);
  const scope = `${companyId}:${roleId}:${mode}`;
  const current = useRef({ scope, canEdit, role });
  current.current = { scope, canEdit: canEdit && snapshot.company.id === companyId, role };
  const alive = useRef(true);
  const request = useRef(0);
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [assignments, setAssignments] = useState<OrganizationPosition['assignments']>(
    role?.assignments || []
  );
  const [parentId, setParentId] = useState(role?.parentPositionId || '');
  const previousParent = useRef(role?.parentPositionId || '');
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      request.current++;
    };
  }, []);
  useEffect(() => {
    request.current++;
    inFlight.current = false;
    setBusy(false);
    setSearch('');
    setParentId(current.current.role?.parentPositionId || '');
    previousParent.current = current.current.role?.parentPositionId || '';
    setError('');
    setConflict(false);
  }, [scope]);
  useEffect(() => {
    setAssignments(role?.assignments || []);
  }, [scope, role?.assignments]);
  useEffect(() => {
    const next = role?.parentPositionId || '';
    const previous = previousParent.current;
    setParentId((value) => (value === previous ? next : value));
    previousParent.current = next;
  }, [role?.parentPositionId]);
  useEffect(() => {
    if (!canEdit || snapshot.company.id !== companyId || !role) onClose();
  }, [canEdit, companyId, snapshot.company.id, role, onClose]);

  const people = useMemo(() => {
    const options = new Map<string, PeopleSelectOption>();
    for (const member of members) {
      if (member.status === 'active')
        options.set(member.id, { id: member.id, name: member.name, photo: member.photo });
    }
    for (const assignment of assignments) {
      if (!options.has(assignment.userId))
        options.set(assignment.userId, {
          id: assignment.userId,
          name:
            assignment.userName ||
            snapshot.people.find((person) => person.id === assignment.userId)?.name ||
            'Former occupant',
          photo:
            assignment.userPhoto ||
            snapshot.people.find((person) => person.id === assignment.userId)?.photo,
        });
    }
    return [...options.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [members, assignments, snapshot.people]);
  const filtered = people.filter((person) =>
    person.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
  const occupants = new Set(assignments.map((assignment) => assignment.userId)).size;
  const excluded = descendantIds(
    snapshot.positions,
    (id) => snapshot.positions.find((item) => item.id === id)?.parentPositionId,
    roleId
  );
  const managers = snapshot.positions.filter((item) => !excluded.has(item.id));
  const invalidSelected = !!parentId && !managers.some((item) => item.id === parentId);
  const level = reportingLevel(snapshot, parentId, roleId);
  const writable = canEdit && !!role && snapshot.company.id === companyId;

  const attempt = async (
    operation: () => Promise<unknown>,
    committed?: (result: unknown) => void
  ) => {
    if (
      !alive.current ||
      !current.current.canEdit ||
      current.current.scope !== scope ||
      !current.current.role ||
      inFlight.current
    )
      return;
    const token = ++request.current;
    const isCurrent = () =>
      alive.current &&
      current.current.scope === scope &&
      current.current.canEdit &&
      token === request.current;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setConflict(false);
    try {
      const result = await operation();
      if (isCurrent()) committed?.(result);
    } catch (failure) {
      if (!isCurrent()) return;
      setError(
        failure instanceof Error ? failure.message : 'The change could not be saved. Try again.'
      );
      setConflict(failure instanceof OrganizationApiError && failure.status === 409);
    } finally {
      if (isCurrent()) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };
  const togglePerson = (person: PeopleSelectOption) => {
    const assignment = assignments.find((item) => item.userId === person.id);
    if (
      !assignment &&
      !members.some((member) => member.id === person.id && member.status === 'active')
    )
      return;
    void attempt(
      () =>
        assignment
          ? mutate(`/org-chart/${companyId}/positions/${roleId}/assign/${assignment.id}`, 'DELETE')
          : mutate(`/org-chart/${companyId}/positions/${roleId}/assign`, 'POST', {
              assignUserId: person.id,
              isPrimary: false,
            }),
      (result) => {
        if (assignment)
          setAssignments((value) => value.filter((item) => item.id !== assignment.id));
        else if (
          result &&
          typeof result === 'object' &&
          'id' in result &&
          typeof result.id === 'string'
        ) {
          const id = result.id;
          setAssignments((value) =>
            value.some((item) => item.userId === person.id)
              ? value
              : [
                  ...value,
                  {
                    id,
                    userId: person.id,
                    userName: person.name,
                    userPhoto: person.photo || undefined,
                    userEmail: '',
                    assignedAt: '',
                    isPrimary: false,
                  },
                ]
          );
        }
      }
    );
  };
  const saveReporting = () => {
    if (invalidSelected || level === null || parentId === (role?.parentPositionId || '')) return;
    void attempt(
      () =>
        mutate(`/org-chart/${companyId}/positions/${roleId}`, 'PUT', {
          parentPositionId: parentId || null,
          level,
        }),
      () => onClose()
    );
  };
  const close = () => {
    if (!inFlight.current) onClose();
  };

  if (!role) return null;
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="org-quick-overlay" />
        <Dialog.Content
          className="org-quick-dialog"
          onCloseAutoFocus={(event) => {
            if (current.current.scope !== scope) return;
            const card = [...document.querySelectorAll<HTMLElement>('[data-role-id]')].find(
              (element) => element.dataset.roleId === roleId
            );
            const trigger = card?.querySelector<HTMLButtonElement>('.org-role-main');
            if (trigger) {
              event.preventDefault();
              trigger.focus();
            }
          }}
          onEscapeKeyDown={(event) => {
            if (inFlight.current) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (inFlight.current) event.preventDefault();
          }}
        >
          <div className="org-quick-header">
            <span className="org-quick-mark">
              {mode === 'people' ? <Users size={18} /> : <Network size={18} />}
            </span>
            <div>
              <Dialog.Title className="org-quick-title">
                {mode === 'people' ? 'Change people' : 'Change reporting line'}
              </Dialog.Title>
              <Dialog.Description className="org-quick-description">
                {role.title}
              </Dialog.Description>
            </div>
            <button
              className="org-quick-close"
              type="button"
              aria-label="Close quick editor"
              disabled={busy}
              onClick={close}
            >
              <X size={18} />
            </button>
          </div>
          {mode === 'people' ? (
            <>
              <div className="org-quick-people-heading">
                <span>
                  {occupants} {occupants === 1 ? 'person' : 'people'} in this role
                </span>
                <span>Changes save immediately</span>
              </div>
              <label className="org-quick-search">
                <Search size={15} aria-hidden="true" />
                <input
                  aria-label="Search people"
                  placeholder="Search people…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <div className="org-quick-people-list">
                {filtered.map((person) => {
                  const assigned = assignments.some((item) => item.userId === person.id);
                  const active = members.some(
                    (member) => member.id === person.id && member.status === 'active'
                  );
                  return (
                    <label
                      className={`org-quick-person ${assigned ? 'is-assigned' : ''}`}
                      key={person.id}
                    >
                      <PersonAvatar person={person} />
                      <span className="org-quick-person-name">
                        {person.name}
                        {!active && <small>Former company member</small>}
                      </span>
                      <input
                        type="checkbox"
                        aria-label={person.name}
                        checked={assigned}
                        disabled={!writable || busy}
                        onChange={() => togglePerson(person)}
                      />
                    </label>
                  );
                })}
                {!filtered.length && (
                  <p className="org-quick-empty">
                    {search ? 'No matching people.' : 'No people available for this role.'}
                  </p>
                )}
              </div>
            </>
          ) : (
            <form
              className="org-quick-reporting"
              onSubmit={(event) => {
                event.preventDefault();
                saveReporting();
              }}
            >
              <label className="org-field">
                <span>Reports to</span>
                <select
                  value={parentId}
                  disabled={!writable || busy}
                  onChange={(event) => {
                    setParentId(event.target.value);
                    setError('');
                  }}
                >
                  <option value="">No reporting manager</option>
                  {invalidSelected && (
                    <option value={parentId} disabled>
                      Current:{' '}
                      {snapshot.positions.find((item) => item.id === parentId)?.title ||
                        'Unavailable role'}{' '}
                      (needs review)
                    </option>
                  )}
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.title}
                    </option>
                  ))}
                </select>
              </label>
              <p className="org-quick-hint">Choose the role this position reports to.</p>
              <button
                type="submit"
                className="org-button org-button-primary"
                disabled={
                  !writable ||
                  busy ||
                  invalidSelected ||
                  level === null ||
                  parentId === (role.parentPositionId || '')
                }
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : null}Save reporting line
              </button>
            </form>
          )}
          {error && (
            <p className="org-quick-error" role="alert">
              {error}
            </p>
          )}
          {conflict && (
            <button
              type="button"
              className="org-button org-button-secondary org-quick-refresh"
              disabled={busy || !writable}
              onClick={() => {
                void attempt(() => Promise.resolve(onRefresh()));
              }}
            >
              Refresh current structure
            </button>
          )}
          {busy && (
            <span className="org-quick-saving" role="status">
              <Loader2 size={13} className="animate-spin" />
              Saving…
            </span>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
